/* Delfino — hero slider (homepage). Slide switching/dots/arrows adapted from
   source/live-supl/dom/home/_inline-12.js (same scrollLeft-based index tracking on the
   scroll-snap track), scoped per [data-dl-hero-slider] instance instead of the live's hardcoded
   data-scope id. "Swipe" is native — the track is overflow-x:auto + scroll-snap-type:x, so touch
   scrolling already works with zero JS (see section-hero-slider.css). Autoplay is new (not on
   the live site): driven by data-autoplay/data-interval, paused on hover/focus.

   Shopify replaces this whole section's markup on every theme-editor edit, but never tears down
   what the OLD node's script attached — nothing else does it for us. Left alone, the autoplay
   timer (plus its hover/focus listeners) keeps running forever on a detached, invisible
   node, and one more accumulates on every edit. shopify:section:unload is dispatched natively by
   Shopify right before a section's old node is discarded (theme.js does not listen for it, so
   each section script that starts a timer is responsible for clearing it itself); every listener
   this file adds is tracked per-instance and torn down there, and data-bound is cleared last so a
   re-bind of the same node (if that node is ever reused) starts clean, with no duplicates.

   Home v2 (2026-09-21) — the "video" slide (nail scanner loop):
   - The <video> ships WITHOUT a src. pickVideoSrc() chooses exactly one file (tall under 769 px,
     wide otherwise) the first time its slide is allowed to play, so the other file is never
     requested. Under prefers-reduced-motion or navigator.connection.saveData nothing is requested
     at all — the <picture> poster underneath stays as the visual.
   - It plays only while its slide is the active one AND the section is on screen AND the tab is
     visible; otherwise it is paused (battery/CPU, and no decoding for something nobody sees).
   - Autoplay is a chained setTimeout, not a fixed-interval timer: every slide carries its own
     data-duration (the video slide stays longer than the product slides). */
(function () {
  'use strict';

  function initHeroSlider(root) {
    root.querySelectorAll('[data-dl-hero-slider]:not([data-bound])').forEach(bindHeroSlider);
  }

  function bindHeroSlider(section) {
    section.dataset.bound = '1';

    var track = section.querySelector('.dl-hero-slides');
    var slides = track ? track.querySelectorAll('.dl-hero-slide') : [];
    var teardown = []; // every listener/timer this bind starts — run in full on unload

    function on(el, evt, handler, opts) {
      el.addEventListener(evt, handler, opts);
      teardown.push(function () { el.removeEventListener(evt, handler, opts); });
    }

    section._dlHeroCleanup = function () {
      teardown.forEach(function (fn) { fn(); });
      teardown.length = 0;
      section._dlHeroCleanup = null;
      delete section.dataset.bound;
    };

    var syncVideos = bindVideos(section, on, teardown);

    if (!track || slides.length < 2) return; // nothing to navigate — single slide or missing track

    var dots = section.querySelectorAll('.dl-hero-dot');
    var prevBtn = section.querySelector('[data-dl-hero-prev]');
    var nextBtn = section.querySelector('[data-dl-hero-next]');

    var currentIndex = function () { return Math.round(track.scrollLeft / track.clientWidth); };
    var setActive = function (idx) {
      dots.forEach(function (dot, i) {
        var isActive = i === idx;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      var changed = false;
      slides.forEach(function (slide, i) {
        if (slide.classList.contains('is-active') !== (i === idx)) changed = true;
        slide.classList.toggle('is-active', i === idx);
      });
      if (changed) {
        syncVideos();
        schedule(); // the new slide gets its own full duration, however it was reached
      }
    };
    var goTo = function (idx) {
      idx = Math.max(0, Math.min(slides.length - 1, idx));
      track.scrollTo({ left: track.clientWidth * idx, behavior: 'smooth' });
    };

    dots.forEach(function (dot, i) {
      on(dot, 'click', function () { goTo(i); });
    });
    if (prevBtn) on(prevBtn, 'click', function () { goTo(currentIndex() - 1); });
    if (nextBtn) on(nextBtn, 'click', function () { goTo(currentIndex() + 1); });

    var ticking = false;
    on(track, 'scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        setActive(currentIndex());
        ticking = false;
      });
    }, { passive: true });

    var timerId = null;
    var paused = false;
    var autoplay = section.dataset.autoplay === 'true';
    function schedule() {
      if (timerId) clearTimeout(timerId);
      timerId = null;
      if (!autoplay) return;
      var idx = currentIndex();
      var slide = slides[idx];
      var wait = Number(slide && slide.dataset.duration) || Number(section.dataset.interval) || 6000;
      timerId = setTimeout(function () {
        timerId = null;
        if (paused || document.hidden) { schedule(); return; }
        goTo(idx >= slides.length - 1 ? 0 : idx + 1);
        // goTo → scroll → setActive → schedule(). If the scroll never happens (reduced-motion
        // browsers that jump without events, a zero-width track), re-arm here as a safety net.
        schedule();
      }, wait);
    }
    if (autoplay) {
      on(section, 'mouseenter', function () { paused = true; });
      on(section, 'focusin', function () { paused = true; });
      on(section, 'mouseleave', function () { paused = false; });
      on(section, 'focusout', function () { paused = false; });
      teardown.push(function () { if (timerId) clearTimeout(timerId); timerId = null; });
      schedule();
    }
  }

  /* Picks the ONE file this device gets. Pure, so the unit test can call it directly. */
  function pickVideoSrc(video, isNarrow) {
    var wide = video.getAttribute('data-src-wide') || '';
    var tall = video.getAttribute('data-src-tall') || '';
    return isNarrow ? (tall || wide) : (wide || tall);
  }

  function motionAllowed(win) {
    var nav = win.navigator || {};
    var conn = nav.connection || nav.mozConnection || nav.webkitConnection;
    if (conn && conn.saveData) return false;
    if (win.matchMedia && win.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    return true;
  }

  function bindVideos(section, on, teardown) {
    var videos = section.querySelectorAll('[data-dl-hero-video]');
    if (!videos.length) return function () {};

    var narrowMq = window.matchMedia ? window.matchMedia('(max-width: 768px)') : null;
    var motionMq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    var inView = !('IntersectionObserver' in window); // no observer → assume visible

    function sync() {
      var allowed = motionAllowed(window);
      videos.forEach(function (video) {
        var slide = video.closest('.dl-hero-slide');
        var shouldPlay = allowed && inView && !document.hidden && (!slide || slide.classList.contains('is-active'));
        if (!shouldPlay) {
          if (!video.paused) video.pause();
          return;
        }
        var want = pickVideoSrc(video, !!(narrowMq && narrowMq.matches));
        if (!want) return;
        if (video.getAttribute('src') !== want) {
          video.muted = true; // the attribute alone is not enough for autoplay policy once src is set from JS
          video.setAttribute('src', want);
          video.load();
        }
        var played = video.play();
        if (played && typeof played.catch === 'function') played.catch(function () {}); // autoplay refused → poster stays
      });
    }

    videos.forEach(function (video) {
      on(video, 'playing', function () { video.classList.add('is-playing'); });
    });

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) { inView = entry.isIntersecting; });
        sync();
      }, { threshold: 0.2 });
      io.observe(section);
      teardown.push(function () { io.disconnect(); });
    }
    on(document, 'visibilitychange', sync);
    [narrowMq, motionMq].forEach(function (mq) {
      if (!mq) return;
      if (mq.addEventListener) on(mq, 'change', sync);
      else if (mq.addListener) { mq.addListener(sync); teardown.push(function () { mq.removeListener(sync); }); }
    });
    teardown.push(function () { videos.forEach(function (video) { video.pause(); }); });

    sync();
    return sync;
  }

  document.addEventListener('shopify:section:unload', function (e) {
    var target = e.target;
    if (!target) return;
    if (target.matches && target.matches('[data-dl-hero-slider]') && typeof target._dlHeroCleanup === 'function') {
      target._dlHeroCleanup();
    }
    if (target.querySelectorAll) {
      target.querySelectorAll('[data-dl-hero-slider]').forEach(function (section) {
        if (typeof section._dlHeroCleanup === 'function') section._dlHeroCleanup();
      });
    }
  });

  // theme.js sits at the very end of <body> (after every section's own markup/scripts, see
  // theme.liquid), so as a *deferred* script it always runs after this one — window.DL does not
  // exist yet at this top-level point, only by DOMContentLoaded (every deferred script, in either
  // order, has run by then). Bind straight away for the current page, and register with DL.use
  // (if DL loaded) so a *future* shopify:section:load — a theme-editor re-render swapping in a
  // brand-new node — gets bound too; initHeroSlider's own [data-dl-hero-slider]:not([data-bound])
  // guard makes calling it here AND via DL.init(document) (which theme.js's own DOMContentLoaded
  // handler triggers) harmless either way.
  document.addEventListener('DOMContentLoaded', function () {
    initHeroSlider(document);
    if (window.DL && typeof window.DL.use === 'function') window.DL.use(initHeroSlider);
  });
})();
